"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlusBold, PiMinusBold } from "react-icons/pi";

interface FaqItem {
  question: string;
  answer: string;
}

export default function OvanFaq({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
      };
    }
  }, []);

  const defaultItems: FaqItem[] = [
    {
      question: "How does it work ?",
      answer: "Instacks provides highly tailored web templates that integrate with Vite and React. Simply select components, configure custom settings, and download clean, production-ready static assets.",
    },
    {
      question: "Do I need a designer to use Instacks ?",
      answer: "No designer is required! The Instacks theme comes pre-packaged with professional layouts, typography, and custom colour palettes designed to engage your visitors instantly.",
    },
    {
      question: "What do I need to do to start selling ?",
      answer: "Configure your shop items or portfolio, hook them to your favorite backend platform, and publish. The layout is optimized to display cards, price badges, and detail overlays.",
    },
    {
      question: "What happens when I receive an order ?",
      answer: "Integrations with leading digital platforms process transaction webhooks, deliver software licenses instantly, and update dashboards with sales analytics.",
    },
  ];

  const items: FaqItem[] = content.items || defaultItems;

  const toggleIndex = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const updateItem = (idx: number, field: keyof FaqItem, value: string) => {
    if (!onContentChange) return;
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange({ ...content, items: newItems });
  };

  return (
    <section
      className={`py-16 md:py-24 transition-colors duration-200 ${
        isDark ? "bg-slate-900" : "bg-white"
      } ${
        isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
      }`}
      style={{ fontFamily }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 2-column layout: Left = title + accordions, Right = question mark image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left Column */}
          <div>
            {/* Header */}
            <div className="pb-8">
              <h2 className="mb-4 text-2xl md:text-3xl leading-normal font-semibold transition-colors">
                <EditableText
                  value={content.title || "Questions & Answers"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, title: val })
                  }
                  as="span"
                  className={isDark ? "text-white" : "text-slate-900"}
                  placeholder="FAQ Title"
                />
              </h2>
              <p className="text-sm sm:text-base">
                <EditableText
                  value={
                    content.text ||
                    "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"
                  }
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, text: val })
                  }
                  as="span"
                  className={isDark ? "text-slate-400" : "text-slate-600"}
                  placeholder="FAQ Subtitle"
                />
              </p>
            </div>

            {/* Accordions */}
            <div className="space-y-4">
              {items.map((item, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <div
                    key={idx}
                    className={`rounded-xl overflow-hidden shadow-sm transition-all duration-300 border ${
                      isDark ? "border-slate-800 bg-slate-900" : "border-slate-100 bg-white"
                    }`}
                  >
                    <button
                      onClick={() => toggleIndex(idx)}
                      className={`w-full px-6 py-4 flex justify-between items-center text-left transition-colors duration-200 ${
                        isDark ? "bg-slate-800/40 hover:bg-slate-800" : "bg-slate-50 hover:bg-slate-100"
                      }`}
                    >
                      <h5 className="font-semibold text-base transition-colors">
                        <EditableText
                          value={item.question}
                          onChange={(val) => updateItem(idx, "question", val)}
                          as="span"
                          className={isDark ? "text-white" : "text-slate-900"}
                          placeholder="FAQ Question"
                        />
                      </h5>
                      <span className={isDark ? "text-slate-400" : "text-slate-500"}>
                        {isOpen ? <PiMinusBold className="size-4" /> : <PiPlusBold className="size-4" />}
                      </span>
                    </button>
                    {isOpen && (
                      <div className={`px-6 py-4 border-t transition-all ${
                        isDark ? "border-slate-800 bg-slate-900/50 text-slate-300" : "border-slate-100 bg-white text-slate-500"
                      }`}>
                        <p className="text-sm leading-relaxed">
                          <EditableText
                            value={item.answer}
                            onChange={(val) => updateItem(idx, "answer", val)}
                            as="span"
                            placeholder="FAQ Answer"
                          />
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column — Illustration */}
          <div className="flex items-center justify-center lg:justify-end">
            <img
              src={content.image || "/assets/question-mark-ByqjaEue.svg"}
              alt="FAQ Illustration"
              className="w-72 md:w-80 lg:w-96 opacity-90 object-contain max-h-[400px]"
            />
          </div>

        </div>
      </div>
    </section>
  );
}
